/**
 * Maps SSE event types to TanStack Query cache operations.
 *
 * Invalidation events  → queryClient.invalidateQueries()
 * Data events          → queryClient.setQueryData() (chat messages for instant delivery)
 * Meta events          → queryClient.invalidateQueries() (full-sync on reconnect)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { App } from '@capacitor/app';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CACHE_KEYS } from '../config/reactQuery';
import { SSE_EVENT_TYPE } from '../config/sseEventTypes';
import {
  EVERY_QUESTION_SLUG,
  updateQuestionEntity,
  getQuestionEntity,
  interactionsKey,
  invalidateQuestionDetail,
  questionDetailBaseKey,
  questionDetailKey,
  questionInteractionsBaseKey,
  questionInteractionsKey,
  questionByUuidBaseKey,
  questionByUuidKey,
  questionMySpacesFeedKey,
  questionsBaseKey,
  questionsEventFeedBaseKey,
  questionsEventFeedRootKey,
  questionsSpaceKey,
  resolveQuestionBaseKey,
  setQuestionFlag,
  suggestedHelpersKey,
  userQuestionsBaseKey,
  weMetConnectionsKey,
} from '@lib/cache/questions';
import { InteractionType } from '@models/api/interaction';
import { type EventRead } from '@models/api/event';
import {
  applyEventUpdate,
  eventAttendeesBaseKey,
  eventDetailBaseKey,
  eventKey,
  eventsBaseKey,
  eventsFeedCountsBaseKey,
  eventsLiveNowKey,
  eventsLiveRailKey,
  eventsLiveRecapKey,
  eventsTabJustEndedBaseKey,
  eventsUpcomingTailKey,
  myCalendarsBaseKey,
  myCheckedInEventsBaseKey,
  resolveEventBaseKey,
} from '@lib/cache/events';
import {
  communitiesKey,
  communityDetailKey,
  invalidateMembershipQueries,
  spaceEventsKey,
  spaceExternalCalendarsBaseKey,
  spaceLiveProbeBaseKey,
  spaceLiveProbeKey,
  spaceMembersBaseKey,
  spaceTopicsKey,
} from '@lib/cache/spaces';
import { helperFeedBaseKey } from '@lib/cache/helperFeed';
import { detectorMeetverseKey } from '@lib/cache/feed';
import { notificationsBaseKey, notificationCountKey } from '@lib/cache/notifications';
import {
  currentUserKey,
  resolveProfileBaseKey,
  userProfileKey,
  usersBaseKey,
} from '@lib/cache/users';
import { useAuth } from '@hooks/useAuth';
import { AuthSessionState } from '@providers/auth/authTypes';
import { useHelperFeedEnabled } from '@hooks/useHelperFeedEnabled';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { UNREAD_COUNT_KEY } from '@hooks/useUnreadCount';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { pendingHeldSpaces } from '@utils/heldSpaces';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';
import { createArrivalBanners } from '@utils/arrivalBanners';
import {
  buildMatchArrivalTitle,
  isViewingQuestion,
  matchArrivalBannerKey,
} from '@utils/matchArrivalBanner';
import { getUserInitials } from '@utils/userAvatar';
import { firstName } from '@utils/string.utils';
import { getQuestionPath } from '@utils/slugHelpers';
import type { SuggestedHelpersResponse } from '@models/api/helpers';
import type { SSEEvent } from './useEventStream';
import type { Message, MessagesQueryData } from './useChat';
import type { TransformedChatRoom } from '@models/view';

/**
 * True when an SSE echo is THIS viewer's OWN action: the acting user is the current user and the
 * payload carries an add/remove `action`. Used to reconcile a per-viewer flag a broadcast payload
 * can't carry -- the question `user_has_*` flag (INTERACTION_CHANGED) and the event `is_going`
 * flag (EVENT_UPDATED participation) -- across the actor's other open tabs/devices.
 */
function isOwnActorEcho(
  actorUserId: string | null | undefined,
  currentUserId: string | null | undefined,
  action: string | null | undefined,
): boolean {
  return !!actorUserId && actorUserId === currentUserId && !!action;
}

export function useRealtimeInvalidation() {
  const queryClient = useQueryClient();
  const { user, sessionState } = useAuth();
  const currentUserId = user?.id;
  const { trackEvent } = useAmplitudeTracking();
  // Stable across renders (react-router memoizes it and it does not subscribe to location, so
  // it neither churns the handler identity nor re-renders this provider on navigation). Used
  // only inside the arrival-banner tap callback below.
  const navigate = useNavigate();

  // Helper-feed gate (single source of truth): admins (has_extra_options) or the
  // launched flag. Now reactive (per-user), not a module constant, so the SSE
  // handler reads it through a ref to see the CURRENT value WITHOUT re-subscribing
  // the stream: `helperFeedEnabled` must NOT enter `handleEvent`'s dep array (a new
  // handler identity would tear down and re-open the EventSource). The ref is
  // written in an effect, not the render body, per the Rules of React
  // (no-ref-writes-during-render), mirroring useEventStream's onEventRef pattern.
  const helperFeedEnabled = useHelperFeedEnabled();
  const helperFeedEnabledRef = useRef(helperFeedEnabled);
  useEffect(() => {
    helperFeedEnabledRef.current = helperFeedEnabled;
  }, [helperFeedEnabled]);

  // Arrival banners, held off every coordinated surface (@utils/arrivalBanners). This provider lives
  // in RootLayout and does not unmount on an auth change, so the session ending drops them (below);
  // the cleanup is for it going away any other way.
  const [arrivalBanners] = useState(createArrivalBanners);
  useEffect(
    () => () => {
      arrivalBanners.dropAll();
    },
    [arrivalBanners],
  );
  // A session that ends takes its news with it. Sign-out leaves the page (useSignInOut), but an
  // expired session turned guest ("Continue as guest") stays on it (transitionToGuestSession): the
  // session drawer clearing would raise the ended session's banner over the guest. Not on
  // REAUTHENTICATING, which is the same session refreshing.
  useEffect(() => {
    if (
      sessionState === AuthSessionState.SESSION_EXPIRED ||
      sessionState === AuthSessionState.UNAUTHENTICATED
    ) {
      arrivalBanners.dropAll();
    }
  }, [sessionState, arrivalBanners]);
  // Back in the foreground, a banner that arrived too long ago goes: sonner pauses a toast's timer
  // while the page is hidden, so one up as the app went to the background would come back reading
  // as fresh news. `appStateChange` is the one resume signal on both platforms (@capacitor/app's web
  // plugin raises it from visibilitychange), as in useEventStream. `App.addListener` is async and
  // its handle can land after cleanup, hence the `stopped` guard on both sides.
  useEffect(() => {
    let stopped = false;
    let remove: (() => void) | null = null;
    void App.addListener('appStateChange', ({ isActive }) => {
      if (stopped || !isActive) return;
      arrivalBanners.dropStale();
    })
      .then(handle => {
        if (stopped) {
          void handle.remove();
        } else {
          remove = () => void handle.remove();
        }
      })
      .catch((error: unknown) => {
        logger.warn('useRealtimeInvalidation: appStateChange listener failed', {
          error: sanitizeError(error),
        });
      });
    return () => {
      stopped = true;
      remove?.();
    };
  }, [arrivalBanners]);

  const handleEvent = useCallback(
    (event: SSEEvent) => {
      logger.debug(`SSE event: ${event.type}`, event.data);

      // Switch on `event.type` (not a destructured constant) so TypeScript
      // narrows `event.data` to the per-event-type shape from
      // `SSEEventDataMap` inside each case.
      switch (event.type) {
        // ── Full sync (reconnection recovery) ───────────────────────────
        case SSE_EVENT_TYPE.FULL_SYNC:
          void queryClient.invalidateQueries();
          break;

        // ── Connection handshake ────────────────────────────────────────
        // useEventStream filters `connected` upstream so the consumer never
        // sees it. Listed here only to keep the switch exhaustive over
        // SSEEventType so the assertNever in the default branch keeps
        // catching unhandled additions.
        case SSE_EVENT_TYPE.CONNECTED:
          break;

        // ── Questions ───────────────────────────────────────────────────
        // QUESTION_UPDATED / QUESTION_DELETED use the broad ['questions'] prefix
        // invalidation, which covers every question query in the app: meetverse
        // feed, my-spaces-feed, and questionsSpaceKey(spaceId) = ['questions',
        // 'space', spaceId], so those cases need no space-scoped branch. The
        // create-signal cases below (QUESTION_CREATED, FEED_NEW_TOP_POST) are
        // deliberately NARROWER on the question-list side: they poke only the
        // new-posts detector so a new post never reflows the mounted feed
        // mid-scroll. QUESTION_CREATED additionally sweeps the event feed-counts
        // family (#966): see its own case comment below. The ['resolve-question']
        // slug-resolver cache is a separate root NOT under ['questions']; updated/
        // deleted invalidate that prefix too (no slug in the SSE payload; bounded
        // to mounted slug-routed question screens, typically 0-1 per tab).
        case SSE_EVENT_TYPE.QUESTION_CREATED:
          // The backend emits question.created only for spaceless questions: ANYONE
          // (global), event-only SPACE_ONLY/SPACE_WIDE, and MY_NETWORK, scoped by
          // QuestionAudience (backend#619). On the question-list side the handler
          // pokes only the new-posts detector, mirroring FEED_NEW_TOP_POST, so the
          // meetverse feed surfaces new public/network posts via the pill without a
          // mid-scroll reflow. Space and my-spaces families need no sweep:
          // space-linked posts emit space.question_created instead, never both.
          // Event-only posts currently have NO goer-visible live list: the event
          // presence feed only shows questions posted in the event's spaces, and no
          // mounted query lists questions by event linkage. That's a documented gap.
          // When such a surface ships, enrich the payload with eventIds and target
          // that family instead of resurrecting a broad ['questions'] sweep.
          void queryClient.invalidateQueries({ queryKey: detectorMeetverseKey() });
          // Event feed-counts sweep (#966): GET /events/feed-counts is goer+space-
          // scoped, not link-scoped (see eventsFeedCountsBaseKey's docstring), so a
          // post here can move an event's posts pill in ways this handler can't
          // resolve without another round trip. The sibling space.question_created
          // case below carries the identical sweep for space posts. Coarse prefix
          // invalidation only refetches MOUNTED counts queries, so it stays cheap.
          void queryClient.invalidateQueries({ queryKey: eventsFeedCountsBaseKey() });
          break;

        case SSE_EVENT_TYPE.FEED_NEW_TOP_POST:
          // A new post landed near the top of THIS user's personalized feed. The
          // backend targets this event per-recipient (#307), so simply receiving
          // it means "your feed changed" — refresh the new-posts-pill detector.
          // The payload carries only questionId (no QuestionRead), so we
          // invalidate → refetch the detector page rather than write it directly.
          // The detector query stays enabled (polling only as the disconnected
          // fallback), so this invalidate actually refetches.
          void queryClient.invalidateQueries({ queryKey: detectorMeetverseKey() });
          break;

        case SSE_EVENT_TYPE.QUESTION_UPDATED:
          void queryClient.invalidateQueries({ queryKey: questionsBaseKey() });
          // The payload names no slug, so every slug-routed post page is swept.
          void invalidateQuestionDetail(queryClient, {
            questionId: event.data.questionId,
            slug: EVERY_QUESTION_SLUG,
          });
          break;

        case SSE_EVENT_TYPE.QUESTION_DELETED:
          void queryClient.invalidateQueries({ queryKey: questionsBaseKey() });
          if (event.data.questionId) {
            // Its arrival banner would open a page that no longer exists: drop it, waiting or up,
            // and refuse the late arrival of a recompute that was running as the post went.
            arrivalBanners.drop(matchArrivalBannerKey(event.data.questionId));
            // Remove from cache instead of invalidating to avoid a 404 refetch. Both id-keyed
            // entries: the detail, and the post page opened by uuid, which has its own root.
            queryClient.removeQueries({
              queryKey: questionDetailKey(event.data.questionId),
            });
            queryClient.removeQueries({
              queryKey: questionByUuidKey(event.data.questionId),
            });
          }
          // Remove all mounted slug-resolver caches. Any could be showing
          // this now-deleted question. Queries for still-existing questions
          // refetch on next mount. Mirrors the event.deleted handling below.
          queryClient.removeQueries({ queryKey: resolveQuestionBaseKey() });
          break;

        // ── Match arrival (asker-addressed, async-matching #22) ──────────
        case SSE_EVENT_TYPE.QUESTION_MATCHES_UPDATED: {
          // camelCase per the backend SSE convention.
          const questionId = event.data.questionId;
          if (!questionId) break;
          const newHelperUserIds = event.data.newHelperUserIds ?? [];
          // The question's slug when the backend resolved it (nullable). Preferred over the
          // cached entity's slug so the suppression check + banner tap reach the canonical slug
          // URL even when the entity cache is cold (a reload straight onto /questions/<slug>).
          const questionSlug = event.data.questionSlug ?? undefined;

          // Extend the match invalidation so the asker's names source (suggested-helpers) and
          // match-state detail refetch - this runs on EVERY terminal recompute, banner or not
          // (the backend now emits this event on every terminal recompute, including a
          // no-matches result with an empty ids list, so the asker's own searching state always
          // exits via this refetch even when nobody arrived). Mirrors QUESTION_UPDATED's detail
          // sweep, through the same helper: the id-keyed detail (a prefix of the suggested-helpers
          // key, so both refetch), the post page opened by uuid (left out, its refetch-and-upsert
          // never runs and match_status stays 'searching' on that screen), and every slug-routed
          // page, since the payload's slug is not always there.
          void invalidateQuestionDetail(queryClient, {
            questionId,
            slug: EVERY_QUESTION_SLUG,
          });

          // ── Arrival banner ──
          // A no-matches terminal carries an empty ids list - real news (searching is over,
          // handled by the invalidations above) but NOT an arrival, so it must never raise a
          // "someone can help" banner.
          if (newHelperUserIds.length === 0) break;

          // The event is asker-addressed, so ownership is implicit; keep only a CHEAP DEFENSIVE
          // guard - bail solely when the cached entity positively says it is not ours. A cold
          // cache trusts the backend's targeting rather than fetching just to check ownership.
          const entity = getQuestionEntity(queryClient, questionId);
          if (entity && !entity.is_my_question) break;

          // The reveal owns the moment on the question's own screen, so stay silent there.
          // Read the path at event time (not via useLocation) so this handler never subscribes
          // to navigation (rerender-defer-reads). Prefer the payload slug so a cold entity cache
          // still matches the slug-routed URL the asker is watching.
          const slug = questionSlug ?? entity?.slug ?? null;
          if (isViewingQuestion(window.location.pathname, { id: questionId, slug })) {
            break;
          }

          // Names + faces. PRIMARY: the payload's own newHelpers (backend #691) - the matcher
          // holds the arrivals' display rows at publish time, so a genuinely NEW arrival gets
          // named even though it is by definition not yet in the pre-arrival suggested-helpers
          // cache. Neither source is ever written into the query cache here - display only, the
          // invalidations above own list state.
          const payloadHelpers = event.data.newHelpers ?? [];
          let firstNames: string[];
          let avatars: { src?: string; initials: string }[];
          if (payloadHelpers.length > 0) {
            firstNames = payloadHelpers
              .map(h => firstName(h.fullName))
              .filter((name): name is string => !!name);
            avatars = payloadHelpers.map(h => ({
              src: h.profilePicture ?? undefined,
              initials: getUserInitials(h.fullName),
            }));
          } else {
            // FALLBACK (payload field absent - older backend - or `[]` - masked by anonymity
            // rules, or an enrichment failure): the (just-invalidated) suggested-helpers cache,
            // RESTRICTED to the helpers this event actually announced (newHelperUserIds). A warm
            // cache also holds earlier waves, so naming everything would credit the wrong people
            // and overstate the count on a second arrival ("Alice, Bob and 3 others" for one new
            // helper). Filtering to the arrivals means a stale cache that lacks them yields empty
            // names and the honest count fallback; a warm cache that already contains an arrival
            // names them. The refetch above fills the real names in for the tap-through reveal.
            const newHelperIdSet = new Set(newHelperUserIds);
            const arrivals = (
              queryClient.getQueryData<SuggestedHelpersResponse>(suggestedHelpersKey(questionId))
                ?.internal ?? []
            ).filter(helper => newHelperIdSet.has(helper.id));
            firstNames = arrivals
              .map(h => firstName(h.full_name))
              .filter((name): name is string => !!name);
            avatars = arrivals.map(h => ({
              src: h.profile_picture ?? undefined,
              initials: getUserInitials(h.full_name),
            }));
          }

          // One banner per question: a second arrival replaces it, waiting or showing. Its tap
          // navigates, and the celebrations (the Going PIN sheet among them), the share sheet,
          // onboarding and the activation drawers are mounted outside the router, the install and
          // push prompts in RootLayout: over one of them, a tap would open the post behind a sheet
          // that stays up. So it never shows while a coordinated surface holds or waits for the
          // screen, and it is taken down if one opens under it.
          arrivalBanners.raise({
            key: matchArrivalBannerKey(questionId),
            options: {
              avatars: avatars.length > 0 ? avatars : undefined,
              title: buildMatchArrivalTitle(firstNames, newHelperUserIds.length),
              subtitle: entity?.title,
              onClick: () => {
                navigate(getQuestionPath({ id: questionId, slug }));
              },
            },
            // Read again after a wait: the asker may have opened the question meanwhile.
            stillWanted: () =>
              !isViewingQuestion(window.location.pathname, { id: questionId, slug }),
          });
          break;
        }

        // ── Interactions ────────────────────────────────────────────────
        case SSE_EVENT_TYPE.INTERACTION_CHANGED: {
          // Data event (backend #347): the payload carries the question's fresh interaction
          // counts, so we patch them in-place on the normalized entity store (updateQuestionEntity)
          // — no refetch. This retires the broad ['questions'] sweep that used to fan a full-feed
          // refetch out to every connected client on every interaction (thundering-herd, #686).
          //
          // Two things a broadcast counts payload cannot patch in place, handled explicitly:
          //   1. The me_too/i_can_help ROSTERS (who clicked). They aren't in the payload, and
          //      QuestionDetails derives its tab counts from the roster length — so a scalar
          //      patch alone leaves the detail tabs stale. We refetch the questionInteractions
          //      query: targeted + active-only (0-1 observers), NOT the retired herd.
          //   2. The viewer's own user_has_* FLAG (per-viewer, un-broadcastable). When the echo
          //      is the viewer's OWN action (actorUserId === currentUserId, backend#931) we
          //      reconcile it from `action`, so a second tab of the same user fills its button live.
          //
          // Consistency: counts are absolute (last-writer-wins), not deltas, and #686 retired the
          // broad refetch that used to incidentally heal stale counts. A dropped or out-of-order
          // echo can leave a count briefly wrong; it self-heals on the next interaction, the 5-min
          // staleTime refetch-on-focus, or FULL_SYNC on reconnect — accepted trade-off (#686/#931).
          const { questionId, counts, interactionType, actorUserId, action } = event.data;

          if (!questionId) {
            // Nothing to act on. Surface it instead of a silent no-op so a backend payload
            // regression is visible (mirrors the chat_payload_partial signal).
            trackEvent(ANALYTICS_EVENTS.interaction_payload_partial, { has_question_id: false });
            break;
          }

          const reconcilesOwnFlag = isOwnActorEcho(actorUserId, currentUserId, action);

          if (counts) {
            updateQuestionEntity(queryClient, questionId, q => {
              const next = {
                ...q,
                uplifts_count: counts.uplifts_count ?? q.uplifts_count,
                me_too_count: counts.me_too_count ?? q.me_too_count,
                i_can_help_count: counts.i_can_help_count ?? q.i_can_help_count,
              };
              // Reconcile the acting viewer's own flag across their tabs. The === chain narrows
              // the wire string to the flag-bearing InteractionType values (no unchecked cast) and
              // scopes the write to the types that actually carry a user_has_* flag.
              if (
                reconcilesOwnFlag &&
                (interactionType === InteractionType.me_too ||
                  interactionType === InteractionType.i_can_help ||
                  interactionType === InteractionType.bookmark)
              ) {
                setQuestionFlag(next, interactionType, action === 'created');
              }
              return next;
            });
          } else {
            // Degraded fallback: the backend's post-commit count read failed, so there are no
            // counts to patch. Refresh the caches the detail screen + feed actually read —
            // targeted, never the ['questions'] herd. Mirrors QUESTION_UPDATED's detail sweep:
            // the id-keyed detail, the post page opened by uuid (its own root, which the
            // id-keyed key does not reach) and every slug-routed page.
            void invalidateQuestionDetail(queryClient, {
              questionId,
              slug: EVERY_QUESTION_SLUG,
            });
          }

          // Rosters live in a cache the payload can't carry, so refresh them in BOTH branches —
          // but only for the interaction types whose rosters actually change (uplift/bookmark
          // don't touch the me_too/i_can_help lists). An absent interactionType (older backend)
          // refreshes defensively.
          if (
            !interactionType ||
            interactionType === InteractionType.me_too ||
            interactionType === InteractionType.i_can_help
          ) {
            void queryClient.invalidateQueries({ queryKey: questionInteractionsKey(questionId) });
          }
          break;
        }

        // ── We Met (user-to-user connections) ───────────────────────────
        case SSE_EVENT_TYPE.WE_MET_CHANGED:
          // A We Met v2 connection involving THIS user changed (backend #356).
          // v2 lives in its own `we_met_connections` table (not Interaction rows),
          // so it can't ride INTERACTION_CHANGED above. The event is scoped
          // per-recipient (target_user_ids), so merely receiving it means "your
          // connections changed" — no client-side filtering needed.
          //
          // The unfiltered connections list is the single source of truth for the
          // connections tab, the pending red dot (useWeMetPendingIncomingCount), the
          // profile ActionRow (useWeMetConnectionWith), AND the we_met notification's
          // Confirm/Decline action state (a reactive view over the same list). One
          // invalidate lights all of them up live.
          void queryClient.invalidateQueries({ queryKey: weMetConnectionsKey() });
          // Best-effort refresh of the notification surface. The
          // we_met_request/confirmed notification row is written by an async ARQ
          // worker that fires no SSE of its own, so this invalidate races that write:
          // it may run before the row exists and instead catch it on a later refetch
          // (focus / next event). Never wrong, just not guaranteed first-try — the
          // connections list above is the race-free guarantee.
          void queryClient.invalidateQueries({ queryKey: notificationsBaseKey() });
          void queryClient.invalidateQueries({ queryKey: notificationCountKey() });
          break;

        // ── Notifications ───────────────────────────────────────────────
        case SSE_EVENT_TYPE.NOTIFICATION_CREATED:
        case SSE_EVENT_TYPE.NOTIFICATION_UPDATED:
          void queryClient.invalidateQueries({ queryKey: notificationsBaseKey() });
          void queryClient.invalidateQueries({ queryKey: notificationCountKey() });
          break;

        // ── Chat ────────────────────────────────────────────────────────
        case SSE_EVENT_TYPE.CHAT_MESSAGE: {
          const { roomId, messageId, senderId, content, createdAt } = event.data;
          const questionId = event.data.questionId ?? null;

          // Direct cache insertion for instant message delivery (see docs/sse-realtime/decisions.md).
          // Falls back to invalidation if payload is incomplete.
          //
          // `senderId` is guarded with `!== undefined`, NOT truthiness. A masked
          // anonymous message (backend #525) sends an explicit `null` — a
          // complete payload that must still fast-path to instant delivery, and
          // `null !== undefined` passes. A genuine backend field drop sends
          // `undefined`, which falls through to the regression alarm below.
          // Truthiness-gating would wrongly push every masked null into the slow
          // path and trip chat_payload_partial on each one.
          if (roomId && messageId && senderId !== undefined && content && createdAt) {
            const newMessage: Message = {
              id: messageId,
              sender_id: senderId,
              content,
              timestamp: createdAt,
              question_id: questionId,
              // Context fields absent from SSE payload — set to null (not undefined)
              // so strict null checks behave correctly. Filled by background invalidation.
              question_slug: null,
              question_title: null,
              question_content: null,
              event_id: null,
              event_slug: null,
              event_name: null,
            };

            // Insert into matching room-based message caches with dedup.
            // Dedup prevents doubles when the sender gets both their optimistic
            // update (onMutate) and the SSE echo for the same message. Returns
            // true iff the cache actually changed, so the caller can skip the
            // background backfill invalidation on a dedup-hit.
            const insertIntoCache = (queryKey: readonly unknown[]): boolean => {
              let inserted = false;
              queryClient.setQueryData<MessagesQueryData>(queryKey, old => {
                if (!old) return old;
                if (old.messages.some(m => m.id === messageId)) return old;
                inserted = true;
                return { ...old, messages: [...old.messages, newMessage] };
              });
              return inserted;
            };

            // Insert for the exact question-scoped view
            const insertedScoped = insertIntoCache(CACHE_KEYS.roomMessages(roomId, questionId));
            // Also insert into the "all messages" view (null question filter)
            const insertedAll = questionId
              ? insertIntoCache(CACHE_KEYS.roomMessages(roomId))
              : false;

            // SSE payload only carries core fields (id, sender_id, content, timestamp,
            // question_id). The renderer also uses question_title, event_name, etc. for
            // context chips. Schedule a background refetch so those fields get filled in
            // shortly after the instant delivery — but only for caches that actually saw
            // a new insert. On dedup-hit (sender's own SSE echo after onSuccess already
            // landed the rich message), the cache is already canonical; invalidating
            // would trigger a redundant refetch.
            if (questionId && insertedScoped) {
              void queryClient.invalidateQueries({
                queryKey: CACHE_KEYS.roomMessages(roomId, questionId),
              });
            }
            if (questionId && insertedAll) {
              void queryClient.invalidateQueries({
                queryKey: CACHE_KEYS.roomMessages(roomId),
              });
            }

            // Direct / user-anonymous fast path (#614): the SSE payload only carries
            // roomId, so resolve roomId -> otherParticipantId via the already-cached
            // rooms list before mirroring the room-based insert above. No mapping
            // (cold cache, anonymous-unrevealed room) falls through to the
            // unconditional userMessagesBase backfill below — never silently drops
            // the message, and that same backfill fills in the context fields this
            // fast path can't (question_title, event_name, etc.), so no separate
            // invalidate is needed here.
            //
            // Multiple rows in the rooms list can share this physical roomId: one
            // per anonymous virtual thread (its own questionId, otherParticipantId
            // null until revealed) plus the direct conversation row (questionId:
            // null, real otherParticipantId) — applyRevealToRoomsCache in
            // useChat.ts keys the same way when a reveal lands. Match on (roomId,
            // questionId) first so an anonymous thread's still-null id is never
            // confused with a different thread's real id in the same room. Fall
            // back to the room's direct row only when no exact-question row
            // exists.
            if (currentUserId) {
              const roomEntries =
                queryClient
                  .getQueryData<{
                    rooms: TransformedChatRoom[];
                  }>(CACHE_KEYS.userRooms(currentUserId))
                  ?.rooms.filter(r => r.roomId === roomId) ?? [];
              const matchedRoom =
                roomEntries.find(r => (r.questionId ?? null) === questionId) ??
                roomEntries.find(r => !r.isAnonymousConversation);
              const otherUserId = matchedRoom?.otherParticipantId;

              if (otherUserId) {
                insertIntoCache(CACHE_KEYS.userMessages(currentUserId, otherUserId, questionId));
                if (questionId) {
                  insertIntoCache(CACHE_KEYS.userMessages(currentUserId, otherUserId));
                }
              }
            }
          } else if (roomId) {
            // Partial payload with roomId known. Invalidate that room's whole
            // message prefix (all question-scoped variants) so the next refetch
            // repairs delivery.
            void queryClient.invalidateQueries({
              queryKey: CACHE_KEYS.roomMessagesPrefix(roomId),
            });
            // Production observability. The full-payload path above does
            // direct cache insertion for instant delivery; this fallback
            // path means delivery is only as fast as the next refetch.
            // Frequent fires here suggest a backend payload regression
            // dropped one of the required fields.
            //
            // `senderId` counts as missing ONLY when it is `undefined` (a genuine
            // field drop). An explicit `null` is intentional masking (backend
            // #525), never a drop — and masked messages never reach this branch
            // anyway (they pass the guard above). So a null sender never alarms;
            // an undefined one does, preserving the regression signal.
            trackEvent(ANALYTICS_EVENTS.chat_payload_partial, {
              has_room_id: true,
              missing_fields: [
                !messageId && 'messageId',
                senderId === undefined && 'senderId',
                !content && 'content',
                !createdAt && 'createdAt',
              ].filter((f): f is string => typeof f === 'string'),
            });
          } else {
            // No roomId. Oversize `{_truncated: true}` fallback (backend
            // docs/SSE_EVENTS.md) or other incomplete payload. Invalidate
            // the entire `['messages']` prefix so any mounted room-anonymous
            // conversation view refetches. Without this, a truncated chat
            // event would silently miss room-based caches while SSE is live.
            void queryClient.invalidateQueries({ queryKey: CACHE_KEYS.roomMessagesBase });
            trackEvent(ANALYTICS_EVENTS.chat_payload_partial, {
              has_room_id: false,
              truncated: event.data._truncated === true,
            });
          }

          // Backfill/catch-all invalidate for user-based message queries. Covers
          // the cold-cache / no-rooms-match case (the only way the message reaches
          // a userMessages cache then) and fills in context fields (question_title,
          // event_name, etc.) the SSE payload doesn't carry, for the fast-path
          // insert above. The base key is a prefix that sweeps every
          // CACHE_KEYS.userMessages(...) entry, so no separate invalidate is
          // needed alongside that insert.
          void queryClient.invalidateQueries({ queryKey: CACHE_KEYS.userMessagesBase });
          // Refresh chat-list consumers with explicit keys instead of the broad
          // `['chats']` prefix. Both queries below are the only active consumers
          // under that prefix today (`useUnreadCount`, `useUserRooms`); other
          // CACHE_KEYS.chatRoom/chatMessages/chatMetadata keys are defined but
          // unused. Targeting explicitly keeps future additions under `['chats']`
          // from being implicitly swept into every chat-message refetch.
          void queryClient.invalidateQueries({
            queryKey: UNREAD_COUNT_KEY,
            exact: true,
          });
          if (currentUserId) {
            void queryClient.invalidateQueries({
              queryKey: CACHE_KEYS.userRooms(currentUserId),
              exact: true,
            });
          }
          break;
        }
        case SSE_EVENT_TYPE.ROOM_UPDATED: {
          // Same narrowing as CHAT_MESSAGE — target the rooms list directly
          // rather than prefix-matching the whole `['chats']` root.
          if (currentUserId) {
            void queryClient.invalidateQueries({
              queryKey: CACHE_KEYS.userRooms(currentUserId),
              exact: true,
            });
          }
          break;
        }

        // ── Events (Calendar) ───────────────────────────────────────────
        // Event detail has two separate caches: `['event', eventId]` (uuid route)
        // and `['resolve-event', slug]` (slug route). Neither is prefix-matched
        // by `['events']` (distinct roots), so they need explicit invalidation.
        case SSE_EVENT_TYPE.EVENT_CREATED:
        case SSE_EVENT_TYPE.EVENT_UPDATED: {
          const { eventId, spaceId, going_count, actorUserId, action, linkedSpaceIds } = event.data;

          // ── Participation emit (backend PR #600, EVENT_UPDATED only) ─────────────
          // The payload carries the event's absolute post-commit going_count. Its PRESENCE is
          // the discriminator: patch that count in place across every event list + BOTH detail
          // roots (via applyEventUpdate) instead of invalidating — retiring the
          // every-event.updated-refetches-every-list herd (the #686/#931 in-place-patch pattern
          // applied to events). We deliberately do NOT invalidate eventsBaseKey() here; that
          // would reintroduce the herd this whole change exists to kill.
          //
          // The `event.type === EVENT_UPDATED` guard keeps EVENT_CREATED on the invalidation
          // path unconditionally: a brand-new event isn't in any cache yet, so an in-place patch
          // would silently no-op and the event would never surface — it needs the list
          // invalidation below. Every going_count-less EVENT_UPDATED (content edit, or the
          // backend's own post-commit count read failing) also falls through; the fallback
          // additionally reconciles the event presence feed when the emit is a DEGRADED
          // participation echo (actorUserId+action present, count absent), see below.
          if (
            event.type === SSE_EVENT_TYPE.EVENT_UPDATED &&
            typeof going_count === 'number' &&
            eventId
          ) {
            // Actor echo: when this emit is the viewer's OWN Going/un-Going
            // (actorUserId === currentUserId), also flip is_going so their OTHER open
            // tabs/devices update the pill live. A non-actor receiver ignores `action` and uses
            // going_count alone. is_going_locked is left untouched (server-authoritative; brief
            // staleness accepted per spec §6.3). Mirrors INTERACTION_CHANGED's own-flag reconcile.
            const flipsOwnGoing = isOwnActorEcho(actorUserId, currentUserId, action);
            applyEventUpdate(queryClient, eventId, existing => {
              const next = { ...existing, going_count };
              if (flipsOwnGoing) {
                next.is_going = action === 'going';
              }
              return next;
            });

            // The Going roster (who's going) is a user list the scalar payload can't patch. The
            // participation path skips the eventKey(id) invalidation that the local
            // useToggleGoingEvent relies on to sweep the roster by prefix, so refresh it
            // explicitly via its search-less prefix (catches every attendee-search variant).
            void queryClient.invalidateQueries({ queryKey: eventAttendeesBaseKey(eventId) });

            // Event presence feed (questions authored by the people who are Going) lives under
            // ['questions', 'event-feed', id], which applyEventUpdate's event-list sweep does NOT
            // reach. A participation echo changes the going-set, hence the feed's authorship, for
            // EVERY viewer (not just the actor), so reconcile it on all echoes. Same rationale as
            // useToggleGoingEvent.onSettled. Single-event scope, not the retired herd.
            void queryClient.invalidateQueries({ queryKey: questionsEventFeedBaseKey(eventId) });

            // Space-slot probe (space-rail card going count): the space page reads its rail
            // candidates from the per-space `spaceLiveProbeKey` probe for events outside the
            // viewer's my-calendars rail (authed rail blind spots) — a standalone root the
            // applyEventUpdate list-patch above does NOT reach (same "eventsBaseKey can't reach"
            // reason spaceLiveProbeBaseKey is swept separately elsewhere). Patch going_count IN
            // PLACE there too (never a refetch — the #686/#931 herd-free pattern), for the event's
            // own space + approved links, so the card's "N going" moves live for EVERY viewer. The
            // map no-ops when the probe's one row isn't this event; preview_users can't ride a
            // scalar payload, so the facepile faces still heal on the next probe refetch (drafted).
            for (const sid of new Set([...(spaceId ? [spaceId] : []), ...(linkedSpaceIds ?? [])])) {
              queryClient.setQueryData<EventRead[] | undefined>(spaceLiveProbeKey(sid), old =>
                old?.map(e => (e.id === eventId ? { ...e, going_count } : e)),
              );
            }

            // Actor's own echo only: an in-place going_count patch can update a My Calendars row
            // that already exists, but it can NOT add or remove the row itself. On the actor's
            // OTHER devices an un-Going would otherwise leave a contradictory My Calendars row, so
            // reconcile the combined feed. myCalendarsBaseKey (['events', 'my-calendars']) is a
            // strict sub-prefix of eventsBaseKey (['events']), so this refetches only My Calendars,
            // never the general-feed herd this change exists to kill.
            if (flipsOwnGoing) {
              void queryClient.invalidateQueries({ queryKey: myCalendarsBaseKey() });

              // Space-scoped event lists ('going' tab and siblings) are membership-filtered like
              // My Calendars: an in-place patch can't add/remove the row on the actor's other
              // devices. Sweep the primary + approved linked spaces, exactly the set
              // useToggleGoingEvent already sweeps locally. Actor-echo only + per-space keys,
              // so this stays herd-safe.
              for (const sid of new Set([
                ...(spaceId ? [spaceId] : []),
                ...(linkedSpaceIds ?? []),
              ])) {
                void queryClient.invalidateQueries({ queryKey: spaceEventsKey(sid) });
              }
              // The checked-in-events picker (CreateQuestion / Crosspost / WeMet drawers) is a
              // separate root no event-list sweep reaches. The payload can't distinguish
              // check-in from RSVP (both emit action 'going'), so reconcile it on every own
              // echo; the refetch is actor-scoped and cheap.
              void queryClient.invalidateQueries({ queryKey: myCheckedInEventsBaseKey() });
            }
            break;
          }

          // ── Content edit / create / degrade: today's invalidation (unchanged) ────
          // Invalidate all event lists (bookmarked, upcoming, etc.) via prefix
          void queryClient.invalidateQueries({ queryKey: eventsBaseKey() });
          // Uuid-keyed detail cache
          if (eventId) {
            void queryClient.invalidateQueries({ queryKey: eventKey(eventId) });
          }
          // Slug-keyed detail cache. We don't have the slug in the SSE payload,
          // so invalidate via `resolveEventBaseKey()`, the prefix factory that
          // matches every `['resolve-event', slug]` entry. Only mounted queries
          // refetch, so the cost is bounded to whatever event-slug screens are
          // currently open (typically 0-1 per tab).
          void queryClient.invalidateQueries({ queryKey: resolveEventBaseKey() });
          // Invalidate space-scoped event feed if the event belongs to a community
          if (spaceId) {
            void queryClient.invalidateQueries({ queryKey: spaceEventsKey(spaceId) });
          }
          // Degraded participation emit (real going change, backend count read failed ->
          // going_count absent): the main participation path invalidates the event presence
          // feed unconditionally, so the degrade path must too or going-derived authorship
          // goes stale. Content edits (no actorUserId/action) skip it.
          if (event.type === SSE_EVENT_TYPE.EVENT_UPDATED && eventId && actorUserId && action) {
            void queryClient.invalidateQueries({ queryKey: questionsEventFeedBaseKey(eventId) });
          }
          break;
        }

        case SSE_EVENT_TYPE.EVENT_DELETED:
          void queryClient.invalidateQueries({ queryKey: eventsBaseKey() });
          if (event.data.eventId) {
            // Remove (not invalidate) the detail cache to avoid a 404 refetch
            // if the event detail screen is currently mounted. Mirrors the
            // `question.deleted` pattern above.
            queryClient.removeQueries({
              queryKey: eventKey(event.data.eventId),
            });
          }
          // Remove all mounted slug-resolver caches. Any of them could be the
          // one showing this now-deleted event. The queries that belong to
          // still-existing events will refetch on next mount anyway (no harm).
          queryClient.removeQueries({ queryKey: resolveEventBaseKey() });
          if (event.data.spaceId) {
            void queryClient.invalidateQueries({
              queryKey: spaceEventsKey(event.data.spaceId),
            });
          }
          break;

        // ── Event Clock: server-side live transitions (spec §3.2) ────────
        // A backend cron flips an event's phase and emits these so live surfaces
        // flip instantly (the goers-only push arm ships separately). Both types
        // invalidate the SAME minimal set: the SERVER-FILTERED lists whose
        // membership changes at a phase transition, plus the detail record's
        // live_status field. Deliberately NARROW — the negative space is
        // load-bearing: feed-counts don't change at a transition, the client
        // clock keeps deriving phase badges from dates (offline-correct), and the
        // broad eventsBaseKey()/['questions'] sweeps would be a needless herd.
        case SSE_EVENT_TYPE.EVENT_WENT_LIVE:
        case SSE_EVENT_TYPE.EVENT_ENDED:
          // Home live rail + space-slot candidate source.
          void queryClient.invalidateQueries({ queryKey: eventsLiveRailKey() });
          // Meetverse live pills — BOTH of the row's calls: an event going live
          // leaves the upcoming band and joins the live one, so refreshing only
          // the live+soon key would leave the just-departed row in the tail
          // cache until its own staleTime elapsed (and the merge seam would
          // then drop it as a duplicate, silently).
          void queryClient.invalidateQueries({ queryKey: eventsLiveNowKey() });
          void queryClient.invalidateQueries({ queryKey: eventsUpcomingTailKey() });
          // Recap sources (the `ended` transition especially): the dedicated recap
          // feed and the events-tab just-ended strip across every `view` slot.
          void queryClient.invalidateQueries({ queryKey: eventsLiveRecapKey() });
          void queryClient.invalidateQueries({ queryKey: eventsTabJustEndedBaseKey() });
          // Space slot fallback probe (standalone root the eventsBaseKey sweep can't reach).
          void queryClient.invalidateQueries({ queryKey: spaceLiveProbeBaseKey() });
          // Detail record (uuid root) for the live_status field. Only when eventId is
          // present; a truncated payload still flips all the list surfaces above.
          if (event.data.eventId) {
            void queryClient.invalidateQueries({ queryKey: eventKey(event.data.eventId) });
          }
          // Slug-resolver detail family (parity with the sibling event.updated sweep): the
          // slug route reads ['resolve-event', slug], a root eventKey(id) can never reach.
          void queryClient.invalidateQueries({ queryKey: resolveEventBaseKey() });
          break;

        // ── Communities / Spaces ─────────────────────────────────────────
        case SSE_EVENT_TYPE.SPACE_UPDATED: {
          // Invalidate all community lists (member count, settings visible in cards)
          void queryClient.invalidateQueries({ queryKey: communitiesKey() });
          // Invalidate community detail (prefix match catches all slug/id variants)
          void queryClient.invalidateQueries({ queryKey: communityDetailKey() });
          // Space name / image_url are denormalized into QuestionRead.space and
          // rendered on every question card (QuestionCard → SpaceIndicator), across
          // the my-spaces, meetverse, and space-scoped feeds. A rename / image change
          // leaves those cards stale — and useMySpacesFeed now gates its 60s poll off
          // while connected, so there is no time-based floor to heal them. Sweep the
          // whole ['questions'] family (matches SPACE_TOPIC_CHANGED's topic-name
          // rationale and QUESTION_UPDATED's broad sweep). The payload names the Space
          // (`spaceId`, `identifier`), but its cards sit in feeds that are not keyed by
          // Space (my-spaces, meetverse), so the sweep is not scoped tighter; safe because
          // space edits are rare admin actions (no thundering-herd risk, unlike #686) and
          // only mounted question queries actually refetch.
          void queryClient.invalidateQueries({ queryKey: questionsBaseKey() });
          break;
        }

        case SSE_EVENT_TYPE.SPACE_MEMBER_JOINED:
        case SSE_EVENT_TYPE.SPACE_MEMBER_LEFT:
        case SSE_EVENT_TYPE.SPACE_MEMBER_STATUS_CHANGED:
        case SSE_EVENT_TYPE.SPACE_MEMBER_REMOVED: {
          const { spaceId, userId: affectedUserId } = event.data;
          // Backend (himeetball/backend#318) scopes `space.member_*` events to
          // approved members of the affected space + the affected user
          // (`sse_audience/space_member.py:46-65`). Within that audience we
          // still split invalidations by viewer-affectedness:
          //
          // ── All recipients ──────────────────────────────────────────────
          // member roster + denormalized member_count (correlated subquery
          // in backend/spaces.py:351 — changes for every approved peer on
          // any JOINED / LEFT / STATUS_CHANGED-to-or-from-APPROVED / REMOVED).
          //
          // ── Self only ───────────────────────────────────────────────────
          // per-space question + event feeds and the cross-space
          // questionMySpacesFeedKey. The visibility predicate is on the
          // *viewer's* SpaceMember status (backend/questions.py:1418-1427),
          // not the author's — a peer's status change doesn't unhide any
          // content for me, so peer recipients get no useful work from
          // those invalidations.
          //
          // Truncated payload (no userId): we cannot determine self-ness,
          // so we default to peer behaviour and rely on FULL_SYNC on
          // reconnect to backfill the affected user's feed. Truncation is
          // a rare oversize-payload guard; degrading there is acceptable.
          // Bulk approve also sends a deliberate {spaceId}-only space.member_status_changed
          // as a roster refresh; the peer tier is the intended handling for it.
          const isSelf = !!affectedUserId && affectedUserId === currentUserId;

          // Both tiers come from `invalidateMembershipQueries`, shared with
          // useCommunityBySlugOrId's pending-membership poll (TECH-386). Its 'self' tier
          // delegates to `invalidateSpaceMembershipQueries`, the single definition every
          // join/leave mutation uses too, so the echo, the poll and the mutation that
          // caused the transition cannot drift apart. The peer/self split and its rationale live
          // in the helper's docstring.
          invalidateMembershipQueries(queryClient, {
            spaceId,
            scope: isSelf ? 'self' : 'peer',
          });
          break;
        }

        case SSE_EVENT_TYPE.SPACE_TOPIC_CHANGED: {
          const { spaceId: topicSpaceId, identifier: topicSpaceSlug } = event.data;
          // Different consumers key spaceTopics by slug or UUID; invalidate both
          // when present so we hit whichever key shape the caller used.
          if (topicSpaceSlug) {
            void queryClient.invalidateQueries({ queryKey: spaceTopicsKey(topicSpaceSlug) });
          }
          if (topicSpaceId) {
            void queryClient.invalidateQueries({ queryKey: spaceTopicsKey(topicSpaceId) });
            // Topic name is denormalized into question UI (QuestionCard renders
            // question.topic_name); a rename/disable/delete leaves question
            // cards stale. Refresh space-scoped question feeds.
            void queryClient.invalidateQueries({ queryKey: questionsSpaceKey(topicSpaceId) });
          }
          // Cross-space aggregations also surface topic_name in question cards.
          void queryClient.invalidateQueries({ queryKey: questionMySpacesFeedKey() });
          break;
        }

        case SSE_EVENT_TYPE.CALENDAR_SYNC_CHANGED:
          // A calendar sync job finished: refetch the admin's calendar list, which carries
          // `sync_in_progress` and the fresh stamps. The list is keyed by slug-or-id, so the
          // payload's `spaceId` can't name its key; invalidate the family. Only mounted
          // lists refetch.
          void queryClient.invalidateQueries({ queryKey: spaceExternalCalendarsBaseKey() });
          break;

        case SSE_EVENT_TYPE.SPACE_QUESTION_CREATED: {
          // A new question was posted in this space (backend scopes the event to
          // the space's approved members). Refresh the space's own feed and the
          // cross-space my-spaces feed so a mounted feed picks it up. We do NOT
          // touch the meetverse detector here — that is FEED_NEW_TOP_POST's job;
          // doing both would double-refetch the detector for the same post.
          const { spaceId: newQuestionSpaceId, questionId: newQuestionId } = event.data;
          if (newQuestionSpaceId) {
            void queryClient.invalidateQueries({ queryKey: questionsSpaceKey(newQuestionSpaceId) });
          }
          void queryClient.invalidateQueries({ queryKey: questionMySpacesFeedKey() });
          // Event presence feed (event-feed create liveness): this space post belongs in the
          // event feed of any event whose spaces include it (get_event_feed = attendees'
          // posts in the event's spaces). We only have spaceId here, not the event(s), so we
          // invalidate the NARROW event-feed root (never the broad questionsBaseKey()): only
          // MOUNTED event feeds refetch, and get_event_feed decides membership on refetch.
          // Mirrors the author's own onSuccess refresh in useCreateQuestionMutation.
          void queryClient.invalidateQueries({ queryKey: questionsEventFeedRootKey() });
          // Event feed-counts sweep (#966): a post authored by someone in an event's
          // going-set, in one of that event's spaces, counts toward that event's
          // posts pill (GET /events/feed-counts is goer+space-scoped, not
          // link-scoped), and this space can be linked to more than one event. We
          // don't know from this payload which events (if any) share this space, so
          // we sweep the whole feed-counts family rather than resolving that
          // client-side. Coarse prefix invalidation only refetches MOUNTED counts
          // queries, mirroring the QUESTION_CREATED sweep above.
          void queryClient.invalidateQueries({ queryKey: eventsFeedCountsBaseKey() });
          // The post as THIS device has it cached, when the event changes what its detail says.
          // Two cases, both about the viewer's own post, both skipped by everyone else:
          // - Held posts: a host approved the author's join request and the held link was just
          //   published. Only the requester's cache has this Space as a held link on the entity.
          //   The post page and the profile list lose the "Pending approval" badge.
          // - The viewer's own post went live in a Space the cached copy does not list: it was
          //   shared into that Space from another device. The cached detail no longer says where
          //   the post is, and the edit composer fills its Space selection from it. A Space the
          //   copy already lists is the echo of this device's own post, with nothing to refresh.
          if (newQuestionId && newQuestionSpaceId) {
            const cachedPost = getQuestionEntity(queryClient, newQuestionId);
            const wasHeldHere = pendingHeldSpaces(cachedPost?.held_spaces).some(
              space => space.id === newQuestionSpaceId,
            );
            const ownPostGainedSpace =
              cachedPost?.is_my_question === true &&
              !cachedPost.space_summaries.some(space => space.id === newQuestionSpaceId);
            if (cachedPost && (wasHeldHere || ownPostGainedSpace)) {
              void invalidateQuestionDetail(queryClient, {
                questionId: newQuestionId,
                slug: cachedPost.slug ?? EVERY_QUESTION_SLUG,
              });
            }
            if (cachedPost && wasHeldHere) {
              void queryClient.invalidateQueries({ queryKey: userQuestionsBaseKey() });
            }
          }
          break;
        }

        // ── Users (identity propagation, P5) ────────────────────────────
        case SSE_EVENT_TYPE.USER_UPDATED: {
          // Backend broadcasts this on any profile edit (PUT /users/{id}) and
          // on account erasure. Payload is IDs-only by design; we invalidate
          // rather than patch: identity is denormalized under 8 different
          // embedded shapes (user / asked_for_user / preview_users[] / flat
          // rows; full_name vs We Met's name), so a generic patcher is a
          // separate project. These are DELIBERATE family-wide sweeps, not a
          // herd violation: unlike interaction/participation events (frequent,
          // per-entity), profile edits are rare, every member of these
          // families renders author identity, and invalidation only refetches
          // MOUNTED queries. Chat message caches are deliberately absent:
          // messages carry no identity fields (bubbles resolve names from the
          // rooms list), and anonymous-room pseudonyms must NOT refresh from
          // real identity.
          const { userId: updatedUserId } = event.data;
          void queryClient.invalidateQueries({ queryKey: questionsBaseKey() });
          void queryClient.invalidateQueries({ queryKey: userQuestionsBaseKey() });
          void queryClient.invalidateQueries({ queryKey: questionDetailBaseKey() });
          void queryClient.invalidateQueries({ queryKey: questionInteractionsBaseKey() });
          // The post page reads the post under one of two more roots, by slug or by uuid.
          void queryClient.invalidateQueries({ queryKey: resolveQuestionBaseKey() });
          void queryClient.invalidateQueries({ queryKey: questionByUuidBaseKey() });
          void queryClient.invalidateQueries({ queryKey: interactionsKey() });
          void queryClient.invalidateQueries({ queryKey: eventsBaseKey() });
          void queryClient.invalidateQueries({ queryKey: eventDetailBaseKey() });
          void queryClient.invalidateQueries({ queryKey: resolveEventBaseKey() });
          // Standalone root eventsBaseKey() can't reach (same reason the Event
          // Clock case sweeps it): the space-home probe caches EventRead objects
          // whose preview_users feed the LiveCompactCard facepile.
          void queryClient.invalidateQueries({ queryKey: spaceLiveProbeBaseKey() });
          void queryClient.invalidateQueries({ queryKey: weMetConnectionsKey() });
          void queryClient.invalidateQueries({ queryKey: spaceMembersBaseKey() });
          void queryClient.invalidateQueries({ queryKey: communityDetailKey() });
          void queryClient.invalidateQueries({ queryKey: notificationsBaseKey() });
          void queryClient.invalidateQueries({ queryKey: helperFeedBaseKey() });
          void queryClient.invalidateQueries({ queryKey: CACHE_KEYS.blockedUsers });
          void queryClient.invalidateQueries({ queryKey: resolveProfileBaseKey() });
          // The `['users']` root: the user search/autocomplete picker
          // (`useInfiniteUsers`) and the admin users-management list both render
          // full_name + profile_picture straight off it. Sweep so a rename or
          // erasure reaches them live, like every other identity surface here.
          void queryClient.invalidateQueries({ queryKey: usersBaseKey() });
          if (currentUserId) {
            // Rooms list denormalizes participant names/avatars per viewer.
            void queryClient.invalidateQueries({ queryKey: CACHE_KEYS.userRooms(currentUserId) });
          }
          if (updatedUserId) {
            void queryClient.invalidateQueries({ queryKey: userProfileKey(updatedUserId) });
            if (updatedUserId === currentUserId) {
              // My own edit echoed back: heals my OTHER tabs/devices, since
              // AuthProvider reads useCurrentUser's cache directly.
              void queryClient.invalidateQueries({ queryKey: currentUserKey() });
            }
          } else {
            // Family sweeps above still ran; only the targeted keys need the id.
            trackEvent(ANALYTICS_EVENTS.user_payload_partial);
          }
          break;
        }

        // ── Helper feed ──────────────────────────────────────────────────
        case SSE_EVENT_TYPE.HELPER_MATCH_CREATED:
          // The matcher (backend #511) publishes this to a newly-matched
          // helper's stream. Invalidate the `['helperFeed']` family root,
          // which covers BOTH the unseen-count badge and the per-anchor feed
          // lists, so: (a) the badge ticks live, (b) in-view feed pages refresh
          // their row state. We deliberately do NOT inject the new match into
          // the cache: the feed is anchor-pinned (spec §5.4), so a match newer
          // than the pinned anchor must not splice mid-scroll. It surfaces on
          // the next anchor refresh (tab re-open / pull-to-refresh).
          //
          // Gated on the helper-feed gate (read from the ref so the stable
          // handler sees the current per-viewer value): while the feature is off
          // for this viewer a stray match event must NOT tick the bell badge (no
          // "You can help" tab exists to open). Admins (has_extra_options) and a
          // flipped flag turn it on. The case stays in the switch so the
          // assertNever exhaustiveness check still holds; it just no-ops when off.
          if (helperFeedEnabledRef.current) {
            void queryClient.invalidateQueries({ queryKey: helperFeedBaseKey() });
          }
          break;

        default:
          // The switch is exhaustive over SSEEventType, so `event` narrows to
          // `never` here. assertNever turns "added a new SSEEventType without a
          // case" from a silent runtime drop into a compile-time error.
          assertNever(event);
      }
    },
    [navigate, queryClient, currentUserId, trackEvent, arrivalBanners],
  );

  return handleEvent;
}

/** Exhaustiveness check. Forces every SSEEventType to have a switch case
 *  in `useRealtimeInvalidation`'s handler. If a contributor adds a new
 *  type to `SSE_EVENT_TYPE` without wiring a handler, TypeScript surfaces
 *  the gap here at compile time.
 *
 *  We deliberately include only `value.type` in the thrown message, never
 *  the full event via `JSON.stringify`. `useEventStream`'s catch forwards
 *  `handlerErr.message` to Amplitude (`sse_handler_error.error_message`),
 *  and chat payloads under `data.content` would otherwise leak into
 *  analytics. */
function assertNever(value: never): never {
  const type = (value as { type?: unknown }).type;
  const safeType = typeof type === 'string' ? type : 'unknown';
  throw new Error(`SSE: unhandled event type "${safeType}"`);
}
